import React, {useCallback, useEffect, useMemo, useState} from "react";
import {getDegrees} from "../../../../service/dataService";
import {getUserDegrees} from "../../../../service/userService";
import BasicGrid from "../../../grid/BasicGrid";
import {Button, Divider, toaster} from "rsuite";
import SuccessNotification from "../../../notifications/SuccessNotification";
import {
    EDUCATION_ADD_ERROR,
    EDUCATION_ADD_SUCCESS,
    EDUCATION_EDIT_ERROR,
    EDUCATION_EDIT_SUCCESS,
} from "../../../../service/constants";
import ErrorNotification from "../../../notifications/ErrorNotification";
import AddEducationModal from "../../../modals/education/AddEducationModal";
import EditEducationModal from "../../../modals/education/EditEducationModal";
import {parseISO} from "rsuite/cjs/utils/dateUtils";

const defaultColumns = [
    {
        key: 'institution',
        label: 'Institution',
        flexGrow: 2,
    },
    {
        key: 'degreeName',
        label: 'Degree Name',
    },
    {
        key: 'major',
        label: 'Major',
        flexGrow: 2,
    },
    {
        key: 'minor',
        label: 'Minor',
        flexGrow: 2,
    },
    {
        key: 'dateEarned',
        label: 'Date Earned',
    },
];

const UserEditEducation = (props) => {

    const { selectedUser } = props;
    
    const [loading, setLoading] = useState(false);
    const [degrees, setDegrees] = useState([]);
    const [userDegrees, setUserDegrees] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedDegree, setSelectedDegree] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    const loadUserDegrees = useCallback(() => {
        setLoading(true);
        getUserDegrees(selectedUser.id).then((education) => {
            education.forEach((degree) => {
                degree.startDate = parseISO(degree.startDate);
                degree.dateEarned = parseISO(degree.dateEarned);
            });
            setUserDegrees(education);
            setLoading(false);
        });
    }, [selectedUser.id]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EDUCATION_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadUserDegrees();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EDUCATION_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadUserDegrees]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={EDUCATION_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadUserDegrees();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={EDUCATION_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [loadUserDegrees]);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedEducation = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedDegree(rowData);
    },[]);

    useEffect(() => {
        setLoading(true);
        getDegrees().then((data) => {
                // format the data into InputPicker format for now
                setDegrees(data.map(ele => ({label: ele.degreeName, value: ele.degreeName})));
            }
        );
        loadUserDegrees();
    }, [loadUserDegrees]);

    return useMemo(() => {
        return (
            <div className="main-content">
                <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Education</Button>
                <Divider />
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={userDegrees}
                    handleClick={setClickedEducation}
                    exportTitle={`admin-edit-${selectedUser.lastName}-${selectedUser.firstName}-education`}
                />
                <AddEducationModal
                    key={`add-edu-modal-${selectedUser.id}`}
                    open={addModalOpen}
                    handleClose={handleAddClose}
                    degrees={degrees}
                    userId={selectedUser.id}
                />
                <EditEducationModal
                    key={`edit-edu-modal-${selectedUser.id}`}
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    degree={selectedDegree}
                    degrees={degrees}
                    userId={selectedUser.id}
                />
            </div>
        );
    }, [
        addModalOpen,
        columns,
        degrees,
        editModalOpen,
        handleAddClose,
        handleEditClose,
        loading,
        selectedDegree,
        setClickedEducation,
        userDegrees,
        selectedUser.id,
        selectedUser.firstName,
        selectedUser.lastName,
    ]);
}

export default UserEditEducation;